import { Box, Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { useMedia } from "@hooks/useMedia";
import { useStores } from "@stores";

import { MarketSelect } from "@components/MarketSelect";
import { Separator, Tooltip } from "@src/ui-kit";

import type { TFunction } from "i18next";
import { FundingRateDisplay, MarketAddressButton, MarketItemBase } from "./components";
import {
  High24hDisplay,
  Low24hDisplay,
  MarkPriceDisplay,
  OpenInterestDisplay,
  OraclePriceDisplay,
  VolumeDisplay,
} from "./displays";
import {
  FundingRow,
  FundingSection,
  MarketDataContainer,
  MarketDataContainerMobile,
  MobileGrid,
  MobileHeaderSection,
  PriceRow,
  PriceSection,
  StatsSection,
  TooltipText,
  TopRow,
} from "./styles";

// ============================================================================
// Sections with tooltips - observer for dynamic symbol
// ============================================================================

// Mark Price section with tooltip - uses stable symbol
const MarkPriceSection = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  const { marketStore } = useStores();
  const symbol = marketStore.activeMarketSymbol;
  const baseSymbol = symbol ? symbol.split("-")[0] : "";

  return (
    <Tooltip
      side="bottom"
      text={
        <Flex direction="column">
          <TooltipText>{t("market.markPriceTooltip", { symbol: baseSymbol })}</TooltipText>
          <TooltipText>{t("market.markPriceDescription")}</TooltipText>
        </Flex>
      }
    >
      <Box>
        <Flex align="center" px="3">
          <MarkPriceDisplay />
        </Flex>
      </Box>
    </Tooltip>
  );
});

// Oracle Price section with tooltip - uses stable symbol
const OraclePriceSection = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  // const { marketStore } = useStores();
  // const symbol = marketStore.activeMarketSymbol;
  // const baseSymbol = symbol ? symbol.split("-")[0] : "";

  return (
    // <Tooltip
    //   side="bottom"
    //   text={
    //     <Flex direction="column">
    //       <TooltipText>{t("market.oraclePriceTooltip", { symbol: baseSymbol })}</TooltipText>
    //       <TooltipText>
    //         {t("market.oracleProvider")}{" "}
    //         <a href="https://insights.pyth.network/price-feeds" rel="noreferrer" target="_blank">
    //           {t("market.pyth")}
    //         </a>
    //       </TooltipText>
    //     </Flex>
    //   }
    // >
    <Box m="0" p="0">
      <MarketItemBase testId="oracle-price" label={t("market.oraclePrice")}>
        <OraclePriceDisplay />
      </MarketItemBase>
    </Box>
    // </Tooltip>
  );
});

const MarketVolumeSection = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  return (
    <Tooltip side="bottom" text={<TooltipText>{t("market.volumeTooltip")}</TooltipText>}>
      <Box m="0" p="0">
        <MarketItemBase testId="volume-24h" label={t("market.volume24h")}>
          <VolumeDisplay />
        </MarketItemBase>
      </Box>
    </Tooltip>
  );
});

const MarketOpenInterestSection = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  return (
    <Tooltip side="bottom" text={<TooltipText>{t("market.openInterestTooltip")}</TooltipText>}>
      <Box m="0" p="0">
        <MarketItemBase testId="open-interest" label={t("market.openInterest")}>
          <OpenInterestDisplay />
        </MarketItemBase>
      </Box>
    </Tooltip>
  );
});

const MarketFundingRateSection = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  return (
    <Tooltip side="bottom" text={<TooltipText>{t("market.fundingRateTooltip")}</TooltipText>}>
      <Box m="0" p="0">
        <MarketItemBase testId="funding-rate" label={t("market.fundingRate")}>
          <FundingRateDisplay />
        </MarketItemBase>
      </Box>
    </Tooltip>
  );
});

// Mark Price section for mobile with tooltip
const MarkPriceSectionMobile = observer(({ t }: { t: TFunction<"translation", undefined> }) => {
  const { marketStore } = useStores();
  const symbol = marketStore.activeMarketSymbol;
  const baseSymbol = symbol ? symbol.split("-")[0] : "";

  return (
    <Tooltip
      side="bottom"
      text={
        <Flex direction="column">
          <TooltipText>{t("market.markPriceTooltip", { symbol: baseSymbol })}</TooltipText>
          <TooltipText>{t("market.markPriceDescription")}</TooltipText>
        </Flex>
      }
    >
      <Box m="0" p="0">
        <MarketItemBase testId="mark-price" label={t("market.markPrice")}>
          <MarkPriceDisplay />
        </MarketItemBase>
      </Box>
    </Tooltip>
  );
});

// ============================================================================
// Main component
// ============================================================================

// Main component - NOT an observer, completely static container
export const MarketData: React.FC = () => {
  const { t } = useTranslation();
  const { desktop } = useMedia();

  return desktop ? <MarketDataDesktop t={t} /> : <MarketDataMobile t={t} />;
};

// ============================================================================
// Layouts
// ============================================================================

// Desktop layout - completely static, no observer dependencies
const MarketDataDesktop = memo(({ t }: { t: TFunction<"translation", undefined> }) => (
  <MarketDataContainer>
    <MarketSelect data-testid="market-data:market-select" />
    <Separator color="gray-5" orientation="vertical" size="4" decorative />

    {/* Mark Price */}
    <MarkPriceSection t={t} />
    <Separator color="gray-5" orientation="vertical" size="4" decorative />

    {/* Oracle Price */}
    <OraclePriceSection t={t} />
    <Separator color="gray-5" orientation="vertical" size="4" decorative />

    {/* Volume 24h */}
    <MarketVolumeSection t={t} />
    <Separator color="gray-5" orientation="vertical" size="4" decorative />

    {/* Open Interest */}
    <MarketOpenInterestSection t={t} />
    <Separator color="gray-5" orientation="vertical" size="4" decorative />

    {/* Funding Rate */}
    <MarketFundingRateSection t={t} />

    <MarketAddressButton t={t} />
  </MarketDataContainer>
));

// Mobile layout - completely static, no observer dependencies
const MarketDataMobile = memo(({ t }: { t: TFunction<"translation", undefined> }) => (
  <MarketDataContainerMobile data-testid="market-data:mobile">
    <MobileHeaderSection>
      <TopRow>
        <MarketSelect data-testid="market-data:market-select" useMobileSheet />
        <MarketAddressButton t={t} size="2" />
      </TopRow>
      <Separator color="gray-5" m="1" orientation="horizontal" size="4" decorative />
    </MobileHeaderSection>
    <PriceSection>
      <PriceRow>
        <MarkPriceSectionMobile t={t} />
        <MarketItemBase testId="oracle-price" label={t("market.oraclePrice")} mobile>
          <OraclePriceDisplay />
        </MarketItemBase>
      </PriceRow>
    </PriceSection>
    <Separator color="gray-5" m="1" orientation="horizontal" size="4" decorative />
    <StatsSection>
      <MobileGrid>
        <MarketItemBase testId="high-24h" label={t("market.high24h")} mobile>
          <High24hDisplay />
        </MarketItemBase>
        <MarketItemBase testId="low-24h" label={t("market.low24h")} mobile>
          <Low24hDisplay />
        </MarketItemBase>
        <MarketItemBase testId="volume-24h" label={t("market.volume24h")} mobile>
          <VolumeDisplay />
        </MarketItemBase>
        <MarketItemBase testId="open-interest" label={t("market.openInterest")} mobile>
          <OpenInterestDisplay />
        </MarketItemBase>
      </MobileGrid>
    </StatsSection>
    <Separator color="gray-5" m="1" orientation="horizontal" size="4" decorative />
    <FundingSection>
      <FundingRow>
        <MarketItemBase testId="funding-rate" label={t("market.fundingRate")} mobile>
          <FundingRateDisplay />
        </MarketItemBase>
      </FundingRow>
    </FundingSection>
  </MarketDataContainerMobile>
));
